import React from "react";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import { Battery } from "api/models/Battery";
import Typography from "@mui/material/Typography";

interface BatteryFormProps {
  data: Battery;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => void;
  handleRemove: (index: number) => void;
  handleClone?: (index: number) => void;
  index: number;
  isAdmin?: boolean;
}

const BatteryForm: React.FC<BatteryFormProps> = ({
  data,
  handleChange,
  handleRemove,
  handleClone,
  index,
  isAdmin,
}) => {
  const handleNumericChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let value = e.target.value.replace(/,/g, ".");

    const decimalParts = value.split(".");
    if (decimalParts.length > 1 && decimalParts[1].length > 2) {
      value = `${decimalParts[0]}.${decimalParts[1].substring(0, 2)}`;
    }

    const modifiedEvent = {
      target: {
        name: e.target.name,
        value: value,
      },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    handleChange(modifiedEvent, index);
  };

  return (
    <Card variant="elevation" sx={{ boxShadow: 2 }}>
      <CardContent>
        <Box
          sx={{
            width: "100&",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            component="p"
            fontSize="18px"
            display="flex"
            alignItems="center"
          >
            <BatteryChargingFullIcon sx={{ mr: 1 }} />
            Battery {index + 1}
          </Typography>
          <Box sx={{ display: "flex", gap: "0.4rem" }}>
            {handleClone && (
              <IconButton onClick={() => handleClone(index)} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={() => handleRemove(index)} size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="ID"
              name="id"
              size="small"
              value={data.id || ""}
              disabled
            />
          </Grid>
          {isAdmin && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="External ID"
                name="externalId"
                size="small"
                value={data.externalId || ""}
                onChange={(e) => handleChange(e, index)}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Min SOC (%)"
              name="minSoc"
              size="small"
              type="text"
              value={data.minSoc}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01", min: 0, max: 100 }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Max SOC (%)"
              name="maxSoc"
              size="small"
              type="text"
              value={data.maxSoc}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01", min: 0, max: 100 }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Capacity (kWh)"
              name="capacity"
              size="small"
              type="text"
              value={data.capacity}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01" }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Inverter Capacity (kW)"
              name="inverterCapacity"
              size="small"
              type="text"
              value={data.inverterCapacity}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01" }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Max Charge (kW)"
              name="maxCharge"
              size="small"
              type="text"
              value={data.maxCharge}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01" }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Max Discharge (kW)"
              name="maxDischarge"
              size="small"
              type="text"
              value={data.maxDischarge}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01" }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Charging Efficiency (%)"
              name="chargingEfficiency"
              size="small"
              type="text"
              value={data.chargingEfficiency}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01", min: 0, max: 100 }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Discharging Efficiency (%)"
              name="dischargingEfficiency"
              size="small"
              type="text"
              value={data.dischargingEfficiency}
              onChange={handleNumericChange}
              required={true}
              inputProps={{ step: "0.01", min: 0, max: 100 }}
              onWheel={(e) =>
                e.target instanceof HTMLElement && e.target.blur()
              }
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BatteryForm;
